/**
 * Minimal client for the Rankings Better Auth endpoints, proxied at `/api/auth/*` (same origin).
 * Email/password sign-in returns a bearer token via the `set-auth-token` header (bearer plugin);
 * social sign-in relies on the cross-subdomain session cookie after the OAuth redirect.
 */
const TOKEN_KEY = "pra.bearer";

export function getToken(): string | undefined {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function setToken(token: string | undefined): void {
  try {
    if (token === undefined) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage unavailable */
  }
}

export function authHeaders(): Record<string, string> {
  const token = getToken();
  return token === undefined ? {} : { Authorization: `Bearer ${token}` };
}

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(`/api/auth${path}`, {
    ...init,
    credentials: "include",
    headers: { "content-type": "application/json", ...authHeaders(), ...(init.headers ?? {}) },
  });
  const token = response.headers.get("set-auth-token");
  if (token) setToken(token);
  return response;
}

export type AuthError = { code?: string; message: string };

async function readError(response: Response): Promise<AuthError> {
  try {
    const body = (await response.json()) as { code?: string; message?: string };
    return { code: body.code, message: body.message ?? `Request failed (${response.status})` };
  } catch {
    return { message: `Request failed (${response.status})` };
  }
}

export async function signInEmail(email: string, password: string): Promise<void> {
  const response = await authFetch("/sign-in/email", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) throw await readError(response);
}

export async function signUpEmail(name: string, email: string, password: string): Promise<void> {
  const response = await authFetch("/sign-up/email", {
    method: "POST",
    body: JSON.stringify({ name, email, password }),
  });
  if (!response.ok) throw await readError(response);
}

export async function signInSocial(provider: "google" | "discord"): Promise<void> {
  const callbackURL = `${window.location.origin}/`;
  const response = await authFetch("/sign-in/social", {
    method: "POST",
    body: JSON.stringify({ provider, callbackURL }),
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { url?: string; redirect?: boolean };
  if (body.url) window.location.assign(body.url);
}

export async function signOut(): Promise<void> {
  try {
    await authFetch("/sign-out", { method: "POST", body: "{}" });
  } finally {
    setToken(undefined);
  }
}
